import { FC, useContext, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import {
  Button,
  Input,
  Preloader,
  Select,
  Separator,
  BackwardIcon,
  ISelectOption,
  H1,
  InfoIcon,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { AuthContext } from 'auth';
import { ClientCard, ClientSuggest } from 'components';
import { useGetUserByUUID, useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';
import { GET_ROLES, IGetRolesResponse } from 'schemas';
import { openConfirmDialog } from 'services/Confirmation';
import { IRole } from 'types';
import { formatFIO } from 'utils';

import { DEFAULT_VALUES } from './constants';
import {
  useClientData,
  useCreateWorker,
  useDeleteWorker,
  useUpdateWorker,
} from './hooks';
import { IFormValues } from './interfaces';
import s from './Worker.module.scss';
import { WorkerModal } from './WorkerModal';

export const Worker: FC = () => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { me, permissions, isOwner },
  } = useContext(AuthContext);

  const navigate = useNavigate();
  const params = useParams<{ uuid: string }>();
  const paramUUID = params.uuid === 'new' ? null : params.uuid;
  const [openModal, setOpenModal] = useState<boolean>(false);
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { isDirty, isSubmitting },
  } = useForm<IFormValues>({
    mode: 'onChange',
    defaultValues: { ...DEFAULT_VALUES },
  });

  const clientUUID = watch('uuid');

  const { data: loadedWorker, loading: workerLoading } =
    useGetUserByUUID(paramUUID);

  useEffect(() => {
    const worker = loadedWorker?.user;

    const resetData = {
      ...DEFAULT_VALUES,
      first_name: worker?.first_name ?? '',
      last_name: worker?.last_name ?? '',
      middle_name: worker?.middle_name ?? '',
      uuid: worker?.uuid ?? null,
    };

    if (worker && worker.roles) {
      const currentRole = worker.roles.filter(({ id }) => id !== 4)[0];

      resetData.role = currentRole.id;
    }

    reset(resetData);
  }, [loadedWorker?.user.uuid]);

  const createWorker = useCreateWorker(t, navigate);

  const updateWorker = useUpdateWorker(t, navigate);

  const { deleteWorker, loading: deleteWorkerLoading } = useDeleteWorker(
    t,
    navigate
  );

  const { data: loadedRoles } = useQuery<IGetRolesResponse>(GET_ROLES, {
    skip: !permissions?.has('role_browse'),
  });

  const roles: Array<ISelectOption> = useMemo(() => {
    if (loadedRoles?.roles)
      return loadedRoles?.roles
        .filter(({ id }) => id !== 4)
        .map(({ id, alias }) => ({
          value: id,
          title: t(`roles.${alias}`),
          disabled: id === 1,
        }));

    if (loadedWorker && loadedWorker.user) {
      const role = loadedWorker.user.roles[0];

      return [
        {
          value: role.id,
          title: t(`roles.${role.alias}`),
        },
      ];
    }

    return [];
  }, [loadedRoles, t, loadedWorker]);

  const { data: user } = useClientData(clientUUID);

  const size = useWindowResize();

  return (
    <div className={s.container}>
      {openModal && clientUUID && (
        <WorkerModal
          onClose={() => setOpenModal(false)}
          clientUUID={clientUUID}
        />
      )}
      <div className={s.link} onClick={() => navigate('/workers')}>
        <BackwardIcon />
        {t('back')}
      </div>
      <Preloader loading={workerLoading}>
        <div className={s.content}>
          <div className={s.frame}>
            <header className={s.header}>
              <H1>{t(loadedWorker ? 'worker.updating' : 'worker.add')}</H1>
              <span className={s.text}>{t('worker.text')}</span>
            </header>
            <div className={s.data}>
              <form
                onSubmit={handleSubmit(({ role, uuid, ...data }) => {
                  if (!uuid || !role || !permissions?.has('worker_manage')) {
                    return;
                  }

                  if (paramUUID && user && user.id !== undefined) {
                    return updateWorker({
                      variables: {
                        id: user.id,
                        input: {
                          ...data,
                          role_ids: [Number(role)],
                        },
                      },
                    });
                  }

                  if (!user?.phone) {
                    return;
                  }

                  return createWorker({
                    variables: {
                      input: {
                        ...data,
                        phone: user.phone,
                        role_ids: [Number(role)],
                      },
                    },
                  }).catch(console.error);
                })}
                className={s.form}
              >
                <div className={s['fields-group']}>
                  <Controller
                    render={({ field: { value, onChange } }) => (
                      <div className={cn(s['field-container'], s.input)}>
                        <ClientSuggest
                          value={value}
                          onChange={onChange}
                          disabled={Boolean(paramUUID)}
                          filterClientsFn={(client) => !client.roles.length}
                          worker={true}
                          showBorder={true}
                        />
                        {size.width < 960 && clientUUID && (
                          <div
                            onClick={() => setOpenModal(true)}
                            className={s.info}
                          >
                            <InfoIcon color="link-info" />
                          </div>
                        )}
                      </div>
                    )}
                    name="uuid"
                    control={control}
                    rules={{
                      required: t('errors.required'),
                    }}
                  />
                  <Controller
                    render={({ field, fieldState: { error } }) => (
                      <div className={s['field-container']}>
                        <Select
                          {...field}
                          error={error?.message}
                          disabled={
                            !clientUUID ||
                            loadedWorker?.user.roles.some(
                              ({ id }) => id === 1
                            ) ||
                            !permissions?.has('role_browse')
                          }
                          placeholder={t('worker.role')}
                          options={roles}
                          className={s.select}
                        />
                      </div>
                    )}
                    name="role"
                    control={control}
                    rules={{ required: t('errors.required') }}
                  />
                </div>
                <Separator className={s['divider']} />
                <div className={s['fields-group']}>
                  <Controller
                    render={({ field, fieldState: { error } }) => (
                      <div className={s['field-container']}>
                        <Input
                          {...field}
                          variant="secondary"
                          disabled={
                            !clientUUID ||
                            (!isOwner &&
                              loadedWorker?.user.roles.some(
                                ({ id }) => id === 1
                              ))
                          }
                          placeholder={t('surname')}
                          error={error?.message}
                          bordered
                        />
                      </div>
                    )}
                    name="last_name"
                    control={control}
                    rules={{
                      required: t('errors.required'),
                      maxLength: {
                        value: 30,
                        message: t('errors.maxLength', 30),
                      },
                    }}
                  />
                  <Controller
                    render={({ field, fieldState: { error } }) => (
                      <div className={s['field-container']}>
                        <Input
                          {...field}
                          variant="secondary"
                          disabled={
                            !clientUUID ||
                            (!isOwner &&
                              loadedWorker?.user.roles.some(
                                ({ id }) => id === 1
                              ))
                          }
                          placeholder={t('first_name')}
                          error={error?.message}
                          bordered
                        />
                      </div>
                    )}
                    name="first_name"
                    control={control}
                    rules={{
                      required: t('errors.required'),
                      maxLength: {
                        value: 30,
                        message: t('errors.maxLength', 30),
                      },
                    }}
                  />
                  <Controller
                    render={({ field, fieldState: { error } }) => (
                      <div className={s['field-container']}>
                        <Input
                          {...field}
                          variant="secondary"
                          disabled={
                            !clientUUID ||
                            (!isOwner &&
                              loadedWorker?.user.roles.some(
                                ({ id }) => id === 1
                              ))
                          }
                          error={error?.message}
                          placeholder={t('middle_name')}
                          bordered
                        />
                      </div>
                    )}
                    name="middle_name"
                    control={control}
                    rules={{
                      required: t('errors.required'),
                      maxLength: {
                        value: 30,
                        message: t('errors.maxLength', 30),
                      },
                    }}
                  />
                </div>
                <div className={s['buttons']}>
                  {loadedWorker && (
                    <Button
                      onClick={() => {
                        if (!permissions?.has('worker_manage')) return;

                        return openConfirmDialog(t, 'remove', {
                          title: t('worker.deleting'),
                          message: t(
                            'worker.deleting.message',
                            formatFIO(loadedWorker.user)
                          ),
                          acceptTitle: t('worker.deleting.confirm'),
                          declineTitle: t('worker.deleting.cancel'),
                        }).then(({ status }) => {
                          if (status === 'ACCEPTED') {
                            deleteWorker({
                              variables: { id: loadedWorker.user.id },
                            });
                          }
                        });
                      }}
                      variant="outline"
                      color="red"
                      disabled={
                        loadedWorker?.user.roles.some(
                          (role: IRole) =>
                            role.alias === 'active_operator' || role.id === 1
                        ) ||
                        me?.uuid === loadedWorker.user.uuid ||
                        !permissions?.has('worker_manage')
                      }
                      fullWidth
                    >
                      {t('worker.delete')}
                    </Button>
                  )}
                  <Button
                    type="submit"
                    className={s.send}
                    isLoading={isSubmitting || deleteWorkerLoading}
                    disabled={!isDirty || !permissions?.has('worker_manage')}
                    fullWidth
                  >
                    {t(loadedWorker ? 'save' : 'worker.create')}
                  </Button>
                </div>
              </form>
            </div>
          </div>

          {clientUUID && size.width > 960 && (
            <ClientCard
              notShowButtonDeposit={true}
              hidePausableTariff={true}
              clientUUID={clientUUID}
              showResetPassword={false}
            />
          )}
        </div>
      </Preloader>
    </div>
  );
};
